/**
 * Predictive search in the header drawer.
 *
 * Shopify renders the result list from `sections/predictive-search.liquid`, so the markup,
 * the money format and the image sizes are the theme's own and there is no second
 * implementation of a product row living in JavaScript.
 *
 * The form still submits. Everything here is an accelerator on top of a working search
 * box, and with JavaScript off the drawer behaves exactly as it did before.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;

    this.controller = null;
    this.timer = null;

    this.input.addEventListener('input', () => this.schedule());
    /* Escape clears the suggestions before it reaches the drawer, so the first press
       tidies the panel and the second closes it. */
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.results.innerHTML.trim()) return;
      event.stopPropagation();
      this.clear();
    });
  }

  schedule() {
    clearTimeout(this.timer);
    /* Long enough that a shopper typing "protector" costs one request rather than nine,
       short enough that the list feels like it is keeping up. */
    this.timer = setTimeout(() => this.search(), 250);
  }

  clear() {
    this.results.innerHTML = '';
    this.removeAttribute('data-open');
  }

  async search() {
    const terms = this.input.value.trim();
    /* One or two letters match most of a 15-product catalogue, which is the same as
       matching nothing. */
    if (terms.length < 3) return this.clear();

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product',
      'resources[limit]': this.dataset.limit || '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);

      this.results.innerHTML = await response.text();
      this.toggleAttribute('data-open', Boolean(this.results.innerHTML.trim()));
    } catch (error) {
      /* An aborted request is the normal case — the shopper typed another letter. A
         failed one leaves the submit button, which is the whole search. */
      if (error.name !== 'AbortError') this.clear();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
